import { Link } from 'react-router-dom';
import {
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  Droplet,
  Droplets,
  FlaskConical,
  HeartPulse,
  PersonStanding,
  Pill,
  ScanLine,
  Scissors,
  Stethoscope,
  Utensils,
  Wind,
  type LucideIcon,
} from 'lucide-react';
import type { IpdOrderKind } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { cn } from '@/lib/utils';
import { EmptyState } from '@/components/ui/empty-state';
import { kindLabel } from './order-labels';
import type { ChooserEntry, TypedOrderKind } from './order-form';

export const ORDER_KIND_ICONS: Record<IpdOrderKind, LucideIcon> = {
  medication: Pill,
  iv_fluid: Droplets,
  diet: Utensils,
  nursing: ClipboardCheck,
  procedure: Scissors,
  lab: FlaskConical,
  radiology: ScanLine,
  blood_transfusion: Droplet,
  oxygen: Wind,
  physiotherapy: PersonStanding,
  consult: Stethoscope,
  vitals_monitoring: HeartPulse,
};

const TILE =
  'group focus-ring flex h-full items-start gap-3 rounded-xl border bg-card p-4 text-start shadow-sm transition-[border-color,box-shadow,transform] duration-150 hover:border-primary/40 hover:shadow-md motion-safe:active:scale-[0.99]';

function TileBody({ entry }: { entry: ChooserEntry }) {
  const Icon = ORDER_KIND_ICONS[entry.kind];
  return (
    <>
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-5 w-5" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium">{kindLabel(entry.kind)}</span>
        <span className="mt-0.5 block text-sm text-muted-foreground">{tr('ipd', `io_form_desc_${entry.kind}`)}</span>
        {entry.section && <span className="mt-1.5 block text-xs font-medium text-primary">{tr('ipd', 'io_form_opens_section')}</span>}
      </span>
      <ChevronRight className="mt-2 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 rtl:rotate-180 rtl:group-hover:-translate-x-0.5" aria-hidden />
    </>
  );
}

export function OrderChooser({ entries, onChoose, sectionHref }: { entries: readonly ChooserEntry[]; onChoose: (kind: TypedOrderKind) => void; sectionHref: (entry: ChooserEntry) => string }) {
  if (entries.length === 0) return <EmptyState icon={ClipboardList} title={tr('ipd', 'io_form_no_kinds')} description={tr('ipd', 'io_form_no_kinds_hint')} />;
  return (
    <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {entries.map((entry) => (
        <li key={entry.kind}>
          {entry.section ? (
            <Link to={sectionHref(entry)} className={cn(TILE, 'w-full')}>
              <TileBody entry={entry} />
            </Link>
          ) : (
            <button type="button" className={cn(TILE, 'w-full')} onClick={() => onChoose(entry.kind as TypedOrderKind)}>
              <TileBody entry={entry} />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}
